import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '@/styles/phone-input.css';
import { Button } from '@/components/ui/button';
import {
  Calendar as CalendarIcon,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Building,
  MapPin,
} from 'lucide-react';
import {
  getCalendars,
  setCalendarForEvents,
  createSchedule,
  getTimezones,
  listCountries,
  updateOrganisationAddress,
  updateOrganisationDetails,
  getOrganisationDetails,
} from '@/client/sdk.gen';
import type {
  CalendarResponse,
  DayOfWeek,
  DayInterval,
  Country,
  OrganisationResponse,
} from '@/client/types.gen';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScheduleEditor } from '@/components/ui/schedule-editor';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

interface CalendarSetupProps {
  isOpen: boolean;
  onComplete: () => void;
  userEmail: string;
  organisationId: string;
  initialStep?: number;
}

const DEFAULT_TIME_SLOTS: Record<DayOfWeek, DayInterval[]> = {
  MONDAY: [{ start_time: '09:00', end_time: '17:00' }],
  TUESDAY: [{ start_time: '09:00', end_time: '17:00' }],
  WEDNESDAY: [{ start_time: '09:00', end_time: '17:00' }],
  THURSDAY: [{ start_time: '09:00', end_time: '17:00' }],
  FRIDAY: [{ start_time: '09:00', end_time: '17:00' }],
  SATURDAY: [],
  SUNDAY: [],
};

// Form schema for site details
const siteDetailsSchema = z.object({
  name: z.string().min(1, 'Site name is required'),
  phone: z.string().min(1, 'Phone number is required'),
  addressLine1: z.string().min(1, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State/Province is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
  countryCode: z.string().min(1, 'Country is required'),
});

type SiteDetailsFormValues = z.infer<typeof siteDetailsSchema>;

export function CalendarSetupOnboarding({
  isOpen,
  onComplete,
  userEmail,
  initialStep = 0,
}: CalendarSetupProps) {
  // Always start with the initialStep that was passed in
  const [step, setStep] = useState<number>(initialStep);
  const [calendars, setCalendars] = useState<CalendarResponse[]>([]);
  const [selectedCalendar, setSelectedCalendar] =
    useState<CalendarResponse | null>(null);
  const [timezones, setTimezones] = useState<string[]>([]);
  const [selectedTimezone, setSelectedTimezone] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weeklySchedule, setWeeklySchedule] =
    useState<Record<DayOfWeek, DayInterval[]>>(DEFAULT_TIME_SLOTS);
  const [weeklyAvailability, setWeeklyAvailability] = useState<
    Record<DayOfWeek, boolean>
  >({
    MONDAY: true,
    TUESDAY: true,
    WEDNESDAY: true,
    THURSDAY: true,
    FRIDAY: true,
    SATURDAY: false,
    SUNDAY: false,
  });

  // Site details form
  const siteForm = useForm<SiteDetailsFormValues>({
    resolver: zodResolver(siteDetailsSchema),
    defaultValues: {
      name: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      countryCode: '',
    },
  });

  // Countries for address form
  const [countries, setCountries] = useState<Country[]>([]);
  // Organization details
  const [orgDetails, setOrgDetails] = useState<OrganisationResponse | null>(
    null
  );

  // On mount, load initial data
  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  // Update form with organization details when they're loaded
  useEffect(() => {
    if (orgDetails) {
      siteForm.setValue('name', orgDetails.name || '');
      siteForm.setValue('phone', orgDetails.phone || '');

      if (orgDetails.address) {
        siteForm.setValue(
          'addressLine1',
          orgDetails.address.address_line_1 || ''
        );
        siteForm.setValue(
          'addressLine2',
          orgDetails.address.address_line_2 || ''
        );
        siteForm.setValue('city', orgDetails.address.city || '');
        siteForm.setValue('state', orgDetails.address.state || '');
        siteForm.setValue('postalCode', orgDetails.address.postal_code || '');
        siteForm.setValue('countryCode', orgDetails.address.country_code || '');
      }
    }
  }, [orgDetails]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch organisation details
      const orgResponse = await getOrganisationDetails();
      if (orgResponse.data) {
        setOrgDetails(orgResponse.data);
      }

      // Fetch calendars
      const calendarResponse = await getCalendars();
      if (calendarResponse.data) {
        setCalendars(calendarResponse.data.calendars);
        // Auto-select the first calendar if available
        if (calendarResponse.data.calendars.length > 0) {
          setSelectedCalendar(calendarResponse.data.calendars[0]);
        }
      }

      // Fetch timezones
      const timezoneResponse = await getTimezones();
      if (timezoneResponse.data && timezoneResponse.data.timezones) {
        setTimezones(timezoneResponse.data.timezones);

        // Try to set timezone from browser or from organization data
        const browserTimezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
        const orgTimezone = orgResponse.data?.timezone;

        if (
          orgTimezone &&
          timezoneResponse.data.timezones.includes(orgTimezone)
        ) {
          setSelectedTimezone(orgTimezone);
        } else if (
          browserTimezone &&
          timezoneResponse.data.timezones.includes(browserTimezone)
        ) {
          setSelectedTimezone(browserTimezone);
        } else if (timezoneResponse.data.timezones.length > 0) {
          setSelectedTimezone(timezoneResponse.data.timezones[0]);
        }
      }

      // Fetch countries for address form
      const countriesResponse = await listCountries();
      if (countriesResponse.data && countriesResponse.data.countries) {
        setCountries(countriesResponse.data.countries);
      }
    } catch (err) {
      setError('Failed to load setup data. Please try again.');
      console.error('Error fetching setup data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSiteDetails = async (values: SiteDetailsFormValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Find the country name based on country code
      const countryName =
        countries.find((c) => c.code === values.countryCode)?.name || '';

      // First create the address
      const addressResponse = await updateOrganisationAddress({
        body: {
          address_line_1: values.addressLine1,
          address_line_2: values.addressLine2 || null,
          city: values.city,
          state: values.state,
          postal_code: values.postalCode,
          country_code: values.countryCode,
          country_name: countryName,
        },
      });

      // Then update the organization details with the address ID
      if (addressResponse.data) {
        await updateOrganisationDetails({
          body: {
            name: values.name,
            phone: values.phone,
            // We don't need to explicitly set the address ID as the backend will handle this
          },
        });

        // Move to the calendar step
        setStep(1);
      }
    } catch (err) {
      setError('Failed to save site details. Please try again.');
      console.error('Error saving site details:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveCalendarSelection = async () => {
    if (!selectedCalendar) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await setCalendarForEvents({
        body: {
          calendar_id: selectedCalendar.id,
        },
      });
      setStep(2); // Move to schedule step
    } catch (err) {
      setError('Failed to set calendar. Please try again.');
      console.error('Error setting calendar for events:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveSchedule = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const scheduleConfigData = {
        week_of_day_configs: Object.entries(weeklyAvailability).map(
          ([day, enabled]) => ({
            day_of_week: day as DayOfWeek,
            intervals: enabled ? weeklySchedule[day as DayOfWeek] : [],
            enabled,
          })
        ),
      };

      // Create the schedule
      const createResponse = await createSchedule({
        body: {
          name: 'Default Schedule',
          schedule_config: scheduleConfigData,
          time_zone: selectedTimezone,
          default_schedule: true,
        },
      });

      if (createResponse.data) {
        // Complete the onboarding process
        onComplete();
      }
    } catch (err) {
      setError('Failed to create schedule. Please try again.');
      console.error('Error creating schedule:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Conditional rendering based on step
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col min-h-screen">
      {/* Header with progress */}
      <header className="border-b py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Bleep Setup</h1>
          <div className="flex items-center space-x-2">
            <div
              className={`h-2 w-10 rounded-full ${
                step >= 0 ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            ></div>
            <div
              className={`h-2 w-10 rounded-full ${
                step >= 1 ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            ></div>
            <div
              className={`h-2 w-10 rounded-full ${
                step >= 2 ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            ></div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto py-8 px-6">
          {isLoading && step === 0 ? (
            <div className="fixed inset-0 bg-background/80 flex items-center justify-center backdrop-blur-sm z-50">
              <div className="flex flex-col items-center space-y-4 bg-background p-8 rounded-lg shadow-lg border">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-lg font-medium">Loading site details...</p>
                <p className="text-muted-foreground text-sm">
                  This may take a moment
                </p>
              </div>
            </div>
          ) : null}

          {step === 0 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-4">Enter site details</h2>
                <p className="text-muted-foreground text-lg mb-8">
                  Provide information about your healthcare center.
                </p>
              </div>

              <Form {...siteForm}>
                <form
                  onSubmit={siteForm.handleSubmit(saveSiteDetails)}
                  className="space-y-8"
                >
                  <div className="space-y-6">
                    <div className="p-6 border rounded-lg">
                      <div className="flex items-center mb-6">
                        <div className="flex-shrink-0 mr-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <Building className="h-5 w-5" />
                          </div>
                        </div>
                        <h3 className="text-xl font-medium">
                          Site Information
                        </h3>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={siteForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2">
                              <FormLabel>Site Name</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    placeholder="Enter site name"
                                    {...field}
                                    disabled={isLoading}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={siteForm.control}
                          name="phone"
                          render={() => (
                            <FormItem>
                              <FormLabel>Phone Number</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Controller
                                    control={siteForm.control}
                                    name="phone"
                                    render={({
                                      field: { value, onChange },
                                    }) => (
                                      <PhoneInput
                                        international
                                        defaultCountry="IN"
                                        value={value}
                                        onChange={onChange}
                                        className="phone-input-dark"
                                        disabled={isLoading}
                                      />
                                    )}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="p-6 border rounded-lg">
                      <div className="flex items-center mb-6">
                        <div className="flex-shrink-0 mr-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <MapPin className="h-5 w-5" />
                          </div>
                        </div>
                        <h3 className="text-xl font-medium">Address</h3>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={siteForm.control}
                          name="addressLine1"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2">
                              <FormLabel>Address Line 1</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    placeholder="Street address"
                                    {...field}
                                    disabled={isLoading}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={siteForm.control}
                          name="addressLine2"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2">
                              <FormLabel>Address Line 2 (Optional)</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    placeholder="Apartment, suite, unit, etc."
                                    {...field}
                                    disabled={isLoading}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={siteForm.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>City</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    placeholder="City"
                                    {...field}
                                    disabled={isLoading}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={siteForm.control}
                          name="state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>State/Province</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    placeholder="State/Province"
                                    {...field}
                                    disabled={isLoading}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={siteForm.control}
                          name="postalCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Postal Code</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    placeholder="Postal code"
                                    {...field}
                                    disabled={isLoading}
                                  />
                                  {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={siteForm.control}
                          name="countryCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Country</FormLabel>
                              <div className="relative">
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  disabled={isLoading}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select a country" />
                                      {isLoading && (
                                        <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />
                                      )}
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-[300px]">
                                    {countries.map((country) => (
                                      <SelectItem
                                        key={country.code}
                                        value={country.code}
                                      >
                                        {country.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-md bg-destructive/15 p-4 text-sm text-destructive border border-destructive/30">
                      {error}
                    </div>
                  )}
                </form>
              </Form>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Set up your calendar
                </h2>
                <p className="text-muted-foreground text-lg mb-8">
                  Connect your calendar to start scheduling appointments with
                  Bleep.
                </p>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-medium mb-4">Your calendar</h3>
                  {calendars.length > 0 ? (
                    <Card className="p-6 border border-border">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-6">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <CalendarIcon className="h-6 w-6" />
                          </div>
                        </div>
                        <div className="flex-grow">
                          <div className="font-medium text-lg">
                            Google Calendar
                          </div>
                          <div className="text-muted-foreground">
                            {userEmail}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            <Check className="h-4 w-4 mr-1" />
                            Connected
                          </span>
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <div className="p-8 text-center border border-dashed rounded-lg">
                      <p className="text-muted-foreground">
                        No calendars found. Please connect a Google calendar.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-medium">Select your calendar</h3>
                  <p className="text-muted-foreground">
                    Choose the calendar where you want new events to be
                    scheduled.
                  </p>

                  <div className="max-w-md">
                    {calendars.length > 0 ? (
                      <Select
                        value={selectedCalendar?.id || ''}
                        onValueChange={(value) => {
                          const calendar = calendars.find(
                            (cal) => cal.id === value
                          );
                          if (calendar) setSelectedCalendar(calendar);
                        }}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a calendar" />
                          {isLoading && (
                            <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {calendars.map((calendar) => (
                            <SelectItem key={calendar.id} value={calendar.id}>
                              {calendar.name || userEmail}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                  </div>

                  <div className="flex items-center mt-4">
                    <Checkbox id="sync" defaultChecked disabled={isLoading} />
                    <Label
                      htmlFor="sync"
                      className="ml-2 text-sm text-muted-foreground"
                    >
                      Any changes made to events in your calendar will sync with
                      Bleep
                    </Label>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-destructive/15 p-4 text-sm text-destructive border border-destructive/30">
                  {error}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Set your availability
                </h2>
                <p className="text-muted-foreground text-lg mb-8">
                  Define when you're typically available for appointments.
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xl font-medium">Time zone</h3>
                  <div className="max-w-md">
                    <Select
                      value={selectedTimezone}
                      onValueChange={setSelectedTimezone}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a time zone" />
                        {isLoading && (
                          <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {timezones.map((timezone) => (
                          <SelectItem key={timezone} value={timezone}>
                            {timezone}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-medium">Weekly hours</h3>
                  <p className="text-muted-foreground">
                    Set when you are typically available for appointments.
                  </p>

                  <ScheduleEditor
                    schedule={weeklySchedule}
                    availability={weeklyAvailability}
                    onScheduleChange={setWeeklySchedule}
                    onAvailabilityChange={setWeeklyAvailability}
                    className="mt-4"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-destructive/15 p-4 text-sm text-destructive border border-destructive/30">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer implementation */}
      <footer className="border-t py-4 px-6">
        <div className="max-w-5xl mx-auto flex justify-between">
          <div>
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={isLoading || isSubmitting}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
          </div>

          {step === 0 ? (
            <Button
              onClick={siteForm.handleSubmit(saveSiteDetails)}
              disabled={isLoading || isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          ) : step === 1 ? (
            <Button
              onClick={saveCalendarSelection}
              disabled={isLoading || isSubmitting || !selectedCalendar}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={saveSchedule}
              disabled={isLoading || isSubmitting || !selectedTimezone}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
